import { types } from '../../duck';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { Icon, useToast } from '@lon/shared/components';
import { useMediaQuery } from '@lon/shared/hooks';
import { showCopyLinkNotification } from '@lon/shared/utils';

const Welcome: FC<types.WelcomeTypes> = ({
  districtName,
  eventUrlCode,
  username,
  password,
  closeAllToasts,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLargerThan768] = useMediaQuery('(min-width: 768px)');

  const [show, setShow] = useState(false);

  const adoptionLoginUrl = `https://${window.location.hostname.replace(
    'local.',
    ''
  )}/mathnation/login/review/${eventUrlCode}`;

  return (
    <Flex direction="column">
      <Flex
        direction="column"
        p={{ base: 0, md: 8 }}
        gap={{ base: 0, md: 4 }}
        borderBottomWidth="1px"
        borderColor="gray.50"
      >
        <Flex
          direction="column"
          px={{ base: 6, md: 6 }}
          py={{ base: 6, md: 4 }}
          gap={4}
          boxShadow={{ base: 'none', md: '0px 5px 20px -2px #2B364612' }}
          borderRadius={{ base: 0, md: '6px' }}
        >
          <Text
            variant="s3"
            color="primary.400"
            maxW={{ base: 'unset', md: '300px' }}
          >
            {isLargerThan768
              ? t('register.welcome')
              : t('register.welcomeMobile')}
          </Text>
          <Flex direction="column">
            <Flex
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={4}
            >
              <Flex
                direction={{ base: 'column', md: 'row' }}
                align={{ base: 'flex-start', md: 'flex-start' }}
                flexShrink={10}
                maxW="calc(100% - 86px)"
              >
                <Text variant="n1" w="200px" minW="200px">
                  {t('register.url')}
                </Text>
                <Text variant="n3" wordBreak="break-all">
                  {adoptionLoginUrl}
                </Text>
              </Flex>
              <Button
                leftIcon={<Icon name="copy" size="lg" />}
                minW="70px"
                variant="link"
                onClick={() => {
                  navigator.clipboard.writeText(adoptionLoginUrl);
                  showCopyLinkNotification(toast);
                }}
                textDecoration="underline"
              >
                {t('register.copy')}
              </Button>
            </Flex>
            <Flex
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={4}
            >
              <Flex
                direction={{ base: 'column', md: 'row' }}
                align={{ base: 'flex-start', md: 'center' }}
                flexShrink={10}
                maxW="calc(100% - 86px)"
              >
                <Text variant="n1" w="200px" minW="200px">
                  {t('register.adoption')}
                </Text>
                <Text variant="n3" wordBreak="break-all">
                  {districtName}
                </Text>
              </Flex>
              <Button
                leftIcon={<Icon name="copy" size="lg" />}
                minW="70px"
                variant="link"
                onClick={() => {
                  navigator.clipboard.writeText(districtName);
                  showCopyLinkNotification(toast);
                }}
                textDecoration="underline"
              >
                {t('register.copy')}
              </Button>
            </Flex>
            <Flex
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={4}
              maxW="full"
            >
              <Flex
                direction={{ base: 'column', md: 'row' }}
                align={{ base: 'flex-start', md: 'center' }}
                flexShrink={10}
                maxW="calc(100% - 86px)"
              >
                <Text variant="n1" w="200px" minW="200px">
                  {t('register.username')}
                </Text>
                <Text
                  variant="n3"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  whiteSpace="nowrap"
                >
                  {username}
                </Text>
              </Flex>
              <Button
                leftIcon={<Icon name="copy" size="lg" />}
                minW="70px"
                variant="link"
                onClick={() => {
                  navigator.clipboard.writeText(username);
                  showCopyLinkNotification(toast);
                }}
                textDecoration="underline"
              >
                {t('register.copy')}
              </Button>
            </Flex>
            <Flex
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={4}
            >
              <Flex
                direction={{ base: 'column', md: 'row' }}
                align={{ base: 'flex-start', md: 'center' }}
                flexShrink={10}
                maxW="calc(100% - 86px)"
              >
                <Text variant="n1" w="200px" minW="200px">
                  {t('register.password')}
                </Text>
                <Flex direction="row" gap={4} align="center">
                  <Text variant="n3">
                    {show ? password : '•'.repeat(password.length)}
                  </Text>
                  <IconButton
                    minHeight="30px"
                    minWidth="30px"
                    height="30px"
                    paddingX={3}
                    paddingY={1.5}
                    variant="iconButton"
                    borderRadius="full"
                    onClick={() => {
                      setShow((state) => !state);
                    }}
                    aria-label="Show/Hide password"
                    icon={
                      <Icon
                        name={show ? 'eyeCrossedOutlined' : 'eyeOutlined'}
                      />
                    }
                  />
                </Flex>
              </Flex>
              <Button
                leftIcon={<Icon name="copy" size="lg" />}
                minW="70px"
                variant="link"
                onClick={() => {
                  navigator.clipboard.writeText(password);
                  showCopyLinkNotification(toast);
                }}
                textDecoration="underline"
              >
                {t('register.copy')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
        <Flex
          direction="column"
          backgroundColor="gray.25"
          p={6}
          gap={4}
          borderRadius={{ base: 0, md: '6px' }}
          mb={{ base: 6, md: 0 }}
        >
          <Text variant="n1">{t('register.terms')}</Text>
          <Flex direction="column" gap={1} maxW="670px">
            {['use', 'allow', 'share', 'trial'].map((description) => (
              <Flex
                key={description}
                gap={3}
                alignItems="flex-start"
                lineHeight="1rem"
              >
                <Box paddingTop={1}>
                  <Icon name="checkCircleOutlined" size="lg" />
                </Box>
                <Text variant="n3">{t(`register.${description}`)}</Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Flex>
      <HStack px={8} py={6} justify="flex-end">
        <Tooltip variant="dark" label={t('register.tooltipLogin')}>
          <Button
            rightIcon={<Icon name="loginOutlined" size="lg" />}
            size="md"
            variant="solid"
            onClick={() => {
              closeAllToasts();
              navigate(
                generatePath('/mathnation/login/review/:eventCode', {
                  eventCode: eventUrlCode,
                })
              );
            }}
          >
            {t('register.logIn')}
          </Button>
        </Tooltip>
      </HStack>
    </Flex>
  );
};

export default Welcome;
